import React, {FC, useEffect, useMemo, useState} from 'react';
import './App.css';
import './i18n/config';
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
import NavigationSteps from "./components/utils/NavigationSteps";
import Step4 from "./components/Step4";
import Step3 from "./components/Step3";
import Step5 from "./components/Step5";
import {
    BookingInterface, 
    FormStateInterface,
    FormType
} from "./types/types";
import SuccessMessage from "./components/SuccessMessage";
import BookingService from "./api/BookingService";
import {useTranslation} from "react-i18next";
import LoadingIndicator from "./components/utils/LoadingIndicator";
import BreadCrumb from "./components/utils/BreadCrumb";
import {GOOGLE_AD_CLICK_SEND_TO, GOOGLE_AD_CONVERSION_ID, logError, stringifyReplacer} from "./data/price";
import store from "./store";
import { flattenObjectOneLevel } from "./components/utils/stateHelper";
import { nextStep, prevStep, setBookingLanguage, setStep } from './stateReducers/globalSlice';
import { useAppSelector, useAppDispatch } from './hooks';
import { initializeMinPlayStart, setFormType } from './stateReducers/deliverySlice';

interface appProps {
    lang?: string,
    formType: FormType
}

const App: FC<appProps> = ({lang = 'en', formType = "Default"}) => {
    const d = useAppDispatch();
    const {t, i18n} = useTranslation();
    const deliveryType: string | null = useAppSelector((state) => { return state.delivery.deliveryType; });
    const maxStepReached: number = useAppSelector((state) => { return state.globals.maxStepReached; });
    const bookingLanguage: string = useAppSelector((state) => { return state.globals.lang; });
    const needInvoice = useAppSelector((state) => { return state.customerDetails.needInvoice; });
    const customerDetails = useAppSelector((state) => { return state.customerDetails; });

    const [step5Valid, setStep5Valid] = useState(false);
    const [signalStep5Highlight, sendSignalStep5Highlight] = useState(false);

    const [nextStepEnabled, enableNextStep] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const [receivedError, setReceivedError] = useState(false);
    const [savingInProgress, setSavingInProgress] = useState(false);
    const [navigationVisible, enableNavigation] = useState(true);
    const [paymentLink, setPaymentLink] = useState("");

    const currentStep = useAppSelector((state) => { return state.globals.currentStep; });

    useEffect(() => {
        d(setBookingLanguage(lang));
        d(setFormType(formType as FormType));
    }, []);
    const getFlatStateSnapshot: () => BookingInterface = () => {
        return flattenObjectOneLevel(store.getState()) as BookingInterface
    };

    useEffect(() => {
        i18n.changeLanguage(bookingLanguage);
    }, [bookingLanguage]);

    const scrollFromIntoView = () => {
        setTimeout(() => {
            const top = document.getElementById('root')?.getBoundingClientRect().top ?? 0;
            window.scrollTo(0, top + window.scrollY);
        }, 200);
    };

    const goNext = () => {
        logError('User moves to step ' + currentStep, getFlatStateSnapshot());

        if (currentStep === 1) {
            d(initializeMinPlayStart({}));
        }

        if (currentStep === 5) {
            if (step5Valid) {
                enableNavigation(false);
                setSavingInProgress(true);
                BookingService.makeBooking(getFlatStateSnapshot()).then((response) => {
                    logError('BookingService.makeBooking response', response.data);
                    // console.log('BookingService.makeBooking response', response);
                    if (response.data.success) {
                        setPaymentLink(response.data.link);
                        setFormSubmitted(true);
                        setSavingInProgress(false);

                        // Handle ad conversions for invoices (non-invoices are handled on the post-payment page)
                        if (
                            response.data.hasOwnProperty('booking_total')
                            && response.data.hasOwnProperty('booking_code')
                            && response.data.booking_code !== ""
                        ) {
                            if (needInvoice) {
                                const transactionId = response.data.booking_code;
                                const bookingTotal = response.data.booking_total ?? 50;
                                const currency = response.data.currency ?? "EUR";
                                
                                handleInvoiceConversionScripts(transactionId, bookingTotal, currency);
                            }
                        } else {
                            console.log("`booking_total` or `booking_code` property doesn't exist in data supplied from"
                                + " server. No ad conversion possible");
                        }

                        // We don't want them to be redirected in case of an invoice because
                        // we already accomplished everything in the form itself.
                        // Currently, the backend still sends a link, but we're ignoring it.
                        if (!needInvoice) {
                            setTimeout(() => {
                                window.location.href = response.data.link;
                            }, 5000)
                        }
                    } else {
                        setFormSubmitted(true);
                        setReceivedError(true);
                        console.error(response.data);
                    }
                });
                scrollFromIntoView();
            } else {
                // Step 5 invalid. Tell step 5 to highlight the invalid areas for the user
                sendSignalStep5Highlight(true);
                // Reset signal. Receiver will know to only listen for true
                setTimeout(() => {
                    sendSignalStep5Highlight(false);
                }, 1000);
            }
        } else {
            // Let users click the next step immediately on step 5, but show them where things are
            // incomplete if they do
            if (currentStep !== 4) {
                enableNextStep(false);
            }
            d(nextStep(null));
            enableNavigation(true);
            setSavingInProgress(false);
            scrollFromIntoView();
        }
    }
    const goPrev = () => {
        d(prevStep(null));
        scrollFromIntoView();
    }

    const isStepValid = (isValid: boolean) => {
        enableNextStep(isValid);
    }

    let debugState = {};

    /**
     * Makes a callback to the server to determine whether or not it is safe to run ad conversion scripts. If so,
     * they are run within this function (both Microsoft and Google). This is only intended for invoice bookings
     * (non-invoice bookings aren't considered "safe"). Non-invoice bookings are handled in the post-payment step by
     * the backend.
     * 
     * @param transactionId - Checkfront code
     * @param bookingTotal - Checkfront's booking total property
     * @param currency - Checkfront's currency_id property
     */
    async function handleInvoiceConversionScripts(
        transactionId: string,
        bookingTotal: number | string,
        currency: string
    ): Promise<void> {
        const conversionSafe = await BookingService.checkCheckfrontConversionSafe(transactionId);
        if (conversionSafe) {
            // Microsoft conversion notification for invoices (NON-INVOICES HANDLED POST-PAYMENT)
            // If this doesn't work, it may be because it's not in the head + some sort of function
            // handling the total amount.
            try {
                // @ts-ignore - uetq will be created or should exist
                window.uetq = window.uetq || [];
                // @ts-ignore - uetq will be created or should exist
                window.uetq.push('event', '', {
                    "revenue_value": bookingTotal,
                    // Can technically have the currency sent over from Checkfront, but not necessary
                    "currency": currency
                });
                console.log('Microsoft conversion tracked')
            } catch (e) {
                console.error("Error occurred during Microsoft conversion", e);
            }

            // Next, Google conversion for invoices
            try {
                // @ts-ignore - gtag defined outside plugin
                gtag('config', GOOGLE_AD_CONVERSION_ID);
                // @ts-ignore - gtag defined outside plugin
                gtag('set', 'user_data', {
                    "email": customerDetails.email,
                    "phone_number": customerDetails.phone
                });
                function gtag_report_conversion(url) {
                    var callback = function () {
                        console.log('gtag conversion tracked');
                        // We don't use this
                        if (typeof(url) != 'undefined') {
                            window.location = url;
                        }
                    };
                    // @ts-ignore - gtag defined outside plugin
                    gtag('event', 'conversion', {
                        'send_to': GOOGLE_AD_CLICK_SEND_TO,
                        'value': bookingTotal,
                        'currency': currency,
                        'transaction_id': transactionId,
                        'event_callback': callback
                    });
                    return false;
                }
                gtag_report_conversion(undefined);
            } catch (e) {
                console.error("Error occurred during Google conversion", e);
            }
        } else {
            console.log("Ad conversion not safe or already done. Ignoring...");
        }

    }

    useEffect(() => {
        const initialState = store.getState();
        debugState = initialState;
        const interval = setInterval(() => {
            debugState = store.getState();
        }, 2000);
      
        return () => clearInterval(interval);
      }, []);
      
    const isDev = 'development' === process.env.NODE_ENV;

    return (
        <div className="p-2 bg-white reservation-form-container">
            <ul className={`inline-flex items-center space-x-1 md:space-x-3 list-none pl-0 breadcrumb-container-${currentStep}`}>
                {[1, 2, 3, 4, 5, 6].map((step) => {
                    return <li key={step}>
                        <BreadCrumb enabled={step <= currentStep} label={t("breadcrumbs.label" + step)}
                                    onClick={() => d(setStep(step))} showDelimeter={step < 6}
                                    stepNumber={step}
                                    currentStep={currentStep}/>
                    </li>
                })}
            </ul>
            {!formSubmitted && <div>
                {currentStep === 1 && <Step1 setStepValidity={isStepValid} />}
                {currentStep === 2 && <Step2 setStepValidity={isStepValid} />}
                {currentStep === 3 && <Step3 setStepValidity={isStepValid} />}
                {currentStep === 4 && <Step4 setStepValidity={isStepValid} />}
                {currentStep === 5 && !savingInProgress &&
                    <Step5
                        setStepValidity={setStep5Valid}
                        signalHighlight={signalStep5Highlight}
                    />}
                {currentStep === 5 && savingInProgress &&
                    <LoadingIndicator text={t("savingBookingInProgress")}/>
                }
                {navigationVisible && <NavigationSteps onNext={() => goNext()}
                                                       onPrev={() => goPrev()}
                                                       nextEnabled={nextStepEnabled}
                                                       prevEnabled={currentStep > 1}/>
                }
            </div>}
            {formSubmitted && !receivedError && <SuccessMessage link={paymentLink}/>}
            {formSubmitted && receivedError && <div dangerouslySetInnerHTML={
                {__html: t("bookingError")}}>
            </div>}
            {isDev && <div>
                <pre>{JSON.stringify(debugState, stringifyReplacer, 2)}</pre>
            </div>}
        </div>
    );
}

export default App;
